import { T } from '@/ui/Text/Text';
import { Flex } from '@/ui/Flex/Flex';

import { useIntl } from 'react-intl';
import {
  IconKey,
  IconLoopVideo,
  IconScreenSmall,
  IconSlideshowSecondary,
} from '@/icons';
import { Input } from '@/ui/Input/Input';
import HorizontalDivider from '@/ui/HorizontalDivider';
import CustomSelect from '@/ui/Select/Select';
import { useEdges, useNodes } from '@xyflow/react';
import { GeneralSettingsType } from '../types';
import { ActionShowSingleScreenNodeType } from '../../Nodes/types';
import FAQSection from '../FAQSection';
import { StyledSettings, StyledSettingsContent } from '../styles';
import { requiresGenericKey } from '../../customHooks/useValidationLogic';

function ShowSlideSettings({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  settings,
  selectedNode,
  updateNodeData,
  disabled,
}: {
  settings: GeneralSettingsType;
  selectedNode: ActionShowSingleScreenNodeType;
  updateNodeData: (id: string, newData: any) => void;
  disabled?: boolean;
}) {
  const intl = useIntl();

  const nodes = useNodes();
  const edges = useEdges();

  // Determine whether a generic key is needed
  const requireGenericKey = requiresGenericKey(selectedNode, nodes, edges);

  // Reset genericKey if it's not needed anymore
  selectedNode.data.options.genericKey = !requireGenericKey
    ? null
    : selectedNode.data.options.genericKey;

  const faqs = [
    ...(requireGenericKey
      ? [
          {
            question: intl.formatMessage({
              defaultMessage:
                'What is the purpose of Generic Key in this block?',
              id: 'rWKcVn',
            }),
            answer: intl.formatMessage({
              defaultMessage:
                'When connected to a Buzzer or Touch block, the Generic Key is used to identify which slide to show. Make sure to use the same Generic Key in the CMS.',
              id: '7TkQ5K',
            }),
          },
        ]
      : []),
    {
      question: intl.formatMessage({
        defaultMessage: 'How does the Loop Video option work?',
        id: 'kVuxez',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'The default allows the CMS to decide whether the video should loop or not. If set to True, the video will loop indefinitely. If set to False, the video will play once and stop.',
        id: '+kJsen',
      }),
    },
    {
      question: intl.formatMessage({
        defaultMessage: 'What is the purpose of the Overlay key?',
        id: 'IKfGTm',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'The Overlay Key defines the key that should be used to identify which slide to use as an overlay. When connected to the "NFC-Reader All Trihows" block, the overlay will be shown on all slides. When connected to other sensors, the overlay will be show for that sensor. Make sure to map the slide to the Generic Sensor and use the same Frame Key in the CMS in the following format: FRAME_(YOUR FRAME KEY).',
        id: 's9qoUR',
      }),
    },
  ];

  const relevantData = selectedNode.data.options;

  const screenOptions = settings.screens.map((screen) => ({
    label: screen.name || `Screen ${screen.screenNr}`,
    value: screen.screenNr,
  }));

  return (
    <StyledSettings data-testid="action-showSingleScreen-settings">
      <Flex align="center" gap={8}>
        <IconSlideshowSecondary />
        <Flex direction="column">
          <T h3>
            {intl.formatMessage({ defaultMessage: 'Show Slide', id: 'LH3xoj' })}
          </T>
        </Flex>
      </Flex>
      <StyledSettingsContent>
        {requireGenericKey && (
          <>
            <Flex direction="column" gap={8}>
              <Flex align="center" justify="space-between">
                <T body1>
                  {intl.formatMessage({
                    defaultMessage: 'Generic Key',
                    id: 'kAqi/W',
                  })}
                </T>
                <Input
                  required
                  icon={<IconKey />}
                  size="small"
                  value={relevantData.genericKey ?? ''}
                  onChange={(e) =>
                    updateNodeData(selectedNode.id, {
                      ...selectedNode.data,
                      options: { ...relevantData, genericKey: e },
                    })
                  }
                  placeholder="Generic Key"
                  disabled={disabled ?? false}
                />
              </Flex>
              {settings.languageSelective && (
                <T body2>
                  {intl.formatMessage({
                    defaultMessage:
                      'Your Generic Key should look like this in the CMS:',
                    id: 'Jc6IMh',
                  })}
                  {` ${relevantData.genericKey ?? 'YOUR_KEY'}|LANGUAGE`}
                </T>
              )}
            </Flex>
            <HorizontalDivider />
          </>
        )}
        <Flex direction="column" gap={8}>
          <Flex justify="space-between" align="center">
            <T body1>
              {intl.formatMessage({
                defaultMessage: 'Loop Video',
                id: '409mki',
              })}
            </T>
            <CustomSelect
              icon={<IconLoopVideo />}
              isSearchable={false}
              size="small"
              placeholder="Loop Video"
              disabled={disabled ?? false}
              options={[
                {
                  label: intl.formatMessage({
                    defaultMessage: 'Define in CMS',
                    id: 'd+qhQa',
                  }),
                  value: null,
                },
                {
                  label: intl.formatMessage({
                    defaultMessage: 'Yes',
                    id: 'a5msuh',
                  }),
                  value: true,
                },
                {
                  label: intl.formatMessage({
                    defaultMessage: 'No',
                    id: 'oUWADl',
                  }),
                  value: false,
                },
              ]}
              value={(() => {
                if (relevantData.loopVideo === null) {
                  return {
                    label: intl.formatMessage({
                      defaultMessage: 'Define in CMS',
                      id: 'd+qhQa',
                    }),
                    value: null,
                  };
                }
                if (relevantData.loopVideo) {
                  return {
                    label: intl.formatMessage({
                      defaultMessage: 'True',
                      id: 'KKkUks',
                    }),
                    value: true,
                  };
                }
                return {
                  label: intl.formatMessage({
                    defaultMessage: 'False',
                    id: 'rxqs5U',
                  }),
                  value: false,
                };
              })()}
              onChange={(e) => {
                updateNodeData(selectedNode.id, {
                  ...selectedNode.data,
                  options: { ...relevantData, loopVideo: e?.value },
                });
              }}
            />
          </Flex>
          <Flex justify="space-between" align="center">
            <T body1>
              {intl.formatMessage({
                defaultMessage: 'Show on Screen',
                id: 'KM9Syp',
              })}
            </T>
            <CustomSelect
              icon={<IconScreenSmall />}
              isSearchable={false}
              size="small"
              placeholder="ScreenNr"
              disabled={disabled ?? false}
              options={screenOptions}
              value={screenOptions.find(
                (option) => option.value === relevantData.renderOnScreen,
              )}
              onChange={(e) => {
                updateNodeData(selectedNode.id, {
                  ...selectedNode.data,
                  options: { ...relevantData, renderOnScreen: e?.value },
                });
              }}
            />
          </Flex>
          <Flex direction="column" gap={4}>
            <T body1>
              {intl.formatMessage({
                defaultMessage: 'Overlay Key',
                id: 'yUyoRk',
              })}
            </T>
            <Input
              icon={<IconKey />}
              size="small"
              value={relevantData.frameType ?? ''}
              disabled={disabled ?? false}
              onChange={(e) =>
                updateNodeData(selectedNode.id, {
                  ...selectedNode.data,
                  options: { ...relevantData, frameType: e },
                })
              }
              placeholder={intl.formatMessage({
                defaultMessage: 'Overlay Key',
                id: 'yUyoRk',
              })}
            />
          </Flex>
        </Flex>
        <HorizontalDivider />
      </StyledSettingsContent>
      <FAQSection faqs={faqs} />
    </StyledSettings>
  );
}
export default ShowSlideSettings;
